/**
 * Channel handling utilities for multi-channel devices
 */

/**
 * Parse channel identifier to extract channel number
 * @param {string} channel - Channel identifier like 'switch:0', 'light:1'
 * @returns {number} Channel number
 */
export function getChannelId(channel) {
	if (!channel || typeof channel !== 'string') return 0;
	const parts = channel.split(':');
	return parts.length > 1 ? parseInt(parts[1]) : 0;
}

/**
 * Get channel key for a device type
 * @param {string} deviceFamily - 'relay', 'dimmer', 'light', 'sensor', 'input'
 * @param {number} channelId - Channel number
 * @returns {string} Channel key like 'switch:0', 'light:1'
 */
export function getChannelKey(deviceFamily, channelId = 0) {
	const familyMap = {
		relay: 'switch',
		dimmer: 'light',
		light: 'light',
		rgbw: 'light',
		sensor: 'sensor',
		input: 'input',
	};

	const prefix = familyMap[deviceFamily] || 'switch';
	return `${prefix}:${channelId}`;
}

/**
 * Generate channel array for multi-channel devices
 * @param {string} deviceFamily - Device family type
 * @param {number} count - Number of channels
 * @returns {string[]} Array of channel identifiers
 */
export function generateChannels(deviceFamily, count = 1) {
	const channels = [];
	for (let i = 0; i < count; i++) {
		channels.push(getChannelKey(deviceFamily, i));
	}
	return channels;
}

/**
 * Check if any channel matches a condition across all channels
 * @param {object} deviceStatus - Device status object
 * @param {string[]} channels - Array of channel identifiers
 * @param {function} predicate - Function to test each channel
 * @returns {boolean}
 */
export function anyChannel(deviceStatus, channels, predicate) {
	return channels.some((channel) => predicate(deviceStatus?.[channel]));
}

/**
 * Check if all channels match a condition
 * @param {object} deviceStatus - Device status object
 * @param {string[]} channels - Array of channel identifiers
 * @param {function} predicate - Function to test each channel
 * @returns {boolean}
 */
export function allChannels(deviceStatus, channels, predicate) {
	return channels.every((channel) => predicate(deviceStatus?.[channel]));
}

/**
 * Count channels matching a condition
 * @param {object} deviceStatus - Device status object
 * @param {string[]} channels - Array of channel identifiers
 * @param {function} predicate - Function to test each channel
 * @returns {number}
 */
export function countChannels(deviceStatus, channels, predicate) {
	return channels.filter((channel) => predicate(deviceStatus?.[channel])).length;
}
